"use client";

import {
  CancelSubscription,
  CheckSubscription,
  ResumeSubscription,
  SubscriptionWithProduct,
} from "@/app/actions/payments/actions";
import { Code, DataList, Flex, Skeleton } from "@radix-ui/themes";
import { User } from "@supabase/supabase-js";
import { fromUnixTime } from "date-fns";
import { useEffect, useState } from "react";
import ShineBorder from "../magicui/shine-border";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

type StateType = {
  error?: string | undefined;
};

export default function Subscription({ user }: { user?: User | null }) {
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithProduct>();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      checkSubscription();
    })();
  }, []);

  const checkSubscription = async () => {
    setLoading(true);
    const { data, error } = await CheckSubscription();
    if (error || !data) {
      console.log(error);
      return;
    }
    setSubscriptions(data);
    console.log(data);
    setLoading(false);
  };

  const handleDialog = (state: boolean) => {
    if (state) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const cancelSubscription = async () => {
    setLoading(true);
    if (!subscriptions) {
      return;
    }

    const { error } = await CancelSubscription(subscriptions.subscription.id);
    if (error) {
      console.log(error);
      setLoading(false);
      return;
    }
    setOpen(false);
    await checkSubscription();
    setLoading(false);
  };

  const resumeSubscription = async () => {
    setLoading(true);
    if (!subscriptions) {
      return;
    }

    const { error } = await ResumeSubscription(subscriptions.subscription.id);
    if (error) {
      console.log(error);
      setLoading(false);
      return;
    }
    setOpen(false);
    await checkSubscription();
    setLoading(false);
  };

  return (
    <>
      <ShineBorder
        className="w-full border p-0"
        color={["#A07CFE", "#FE8FB5", "#FFBE7B"]}
      >
        {loading && (
          <>
            <CardHeader className="w-full">
              <CardTitle>
                <Skeleton className="w-48 h-8 rounded-lg" />
              </CardTitle>
              <CardDescription></CardDescription>
            </CardHeader>
            <CardContent>
              <DataList.Root>
                <DataList.Item>
                  <DataList.Label minWidth="88px">
                    <Skeleton className="w-20 h-6" />
                  </DataList.Label>
                  <DataList.Value>
                    <Skeleton className="w-60 h-6" />
                  </DataList.Value>
                </DataList.Item>{" "}
                <DataList.Item>
                  <DataList.Label minWidth="88px">
                    <Skeleton className="w-24 h-6" />
                  </DataList.Label>
                  <DataList.Value>
                    <Skeleton className="w-48 h-6" />
                  </DataList.Value>
                </DataList.Item>{" "}
                <DataList.Item>
                  <DataList.Label minWidth="88px">
                    <Skeleton className="w-28 h-6" />
                  </DataList.Label>
                  <DataList.Value>
                    <Skeleton className="w-44 h-6" />
                  </DataList.Value>
                </DataList.Item>{" "}
                <DataList.Item>
                  <DataList.Label minWidth="88px">
                    <Skeleton className="w-24 h-6" />
                  </DataList.Label>
                  <DataList.Value>
                    <Skeleton className="w-48 h-6" />
                  </DataList.Value>
                </DataList.Item>{" "}
                <DataList.Item>
                  <DataList.Label minWidth="88px">
                    <Skeleton className="w-20 h-6" />
                  </DataList.Label>
                  <DataList.Value>
                    <Skeleton className="w-52 h-6" />
                  </DataList.Value>
                </DataList.Item>{" "}
              </DataList.Root>
            </CardContent>
            <CardFooter className="border-t px-6 py-4 flex justify-end">
              <Skeleton className="w-48 h-10" />
            </CardFooter>
          </>
        )}
        {!loading && subscriptions && (
          <>
            <CardHeader className="w-full ">
              <CardTitle>{subscriptions.product.name} Subscription</CardTitle>
              <CardDescription>
                {subscriptions.product.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="w-full">
              <DataList.Root>
                <DataList.Item>
                  <DataList.Label minWidth="88px">ID</DataList.Label>
                  <DataList.Value>
                    <Flex align="center" gap="2">
                      <Code variant="ghost">
                        {subscriptions.subscription.id}
                      </Code>
                    </Flex>
                  </DataList.Value>
                </DataList.Item>
                {subscriptions.subscription.cancel_at_period_end && (
                  <DataList.Item>
                    <DataList.Label minWidth="88px">Status</DataList.Label>
                    <DataList.Value>
                      {subscriptions.subscription.cancel_at_period_end ? (
                        <Badge color="green" variant="default">
                          Cancelled
                        </Badge>
                      ) : (
                        <Badge color="red" variant="default">
                          Active
                        </Badge>
                      )}
                    </DataList.Value>
                  </DataList.Item>
                )}
                {!subscriptions.subscription.cancel_at_period_end && (
                  <DataList.Item>
                    <DataList.Label minWidth="88px">Status</DataList.Label>
                    <DataList.Value>
                      {subscriptions.subscription.status === "active" ? (
                        <Badge color="green" variant="default">
                          Active
                        </Badge>
                      ) : (
                        <Badge color="red" variant="default">
                          {subscriptions.subscription.status}
                        </Badge>
                      )}
                    </DataList.Value>
                  </DataList.Item>
                )}
                <DataList.Item>
                  <DataList.Label minWidth="88px">Amount</DataList.Label>
                  <DataList.Value>
                    {subscriptions.subscription.items.data[0].price.unit_amount
                      ? new Intl.NumberFormat(undefined, {
                          style: "currency",
                          currency:
                            subscriptions.subscription.items.data[0].price
                              .currency,
                        }).format(
                          subscriptions.subscription.items.data[0].price
                            .unit_amount / 100,
                        )
                      : "N/A"}
                  </DataList.Value>
                </DataList.Item>

                {!subscriptions.subscription.cancel_at_period_end && (
                  <>
                    <DataList.Item>
                      <DataList.Label minWidth="88px">
                        Billing Period
                      </DataList.Label>
                      <DataList.Value>
                        {fromUnixTime(
                          subscriptions.subscription.current_period_start,
                        ).toLocaleDateString("en-GB")}{" "}
                        -{" "}
                        {fromUnixTime(
                          subscriptions.subscription.current_period_end,
                        ).toLocaleDateString("en-GB")}
                      </DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label minWidth="88px">Started</DataList.Label>
                      <DataList.Value>
                        {fromUnixTime(
                          subscriptions.subscription.start_date,
                        ).toLocaleDateString("en-GB")}
                      </DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label minWidth="88px">
                        Billing Cycle
                      </DataList.Label>
                      <DataList.Value>
                        per{" "}
                        {
                          subscriptions.subscription.items.data[0].price
                            .recurring?.interval
                        }
                      </DataList.Value>
                    </DataList.Item>
                  </>
                )}
                {subscriptions.subscription.cancel_at && (
                  <>
                    <DataList.Item>
                      <DataList.Label minWidth="88px">Started</DataList.Label>
                      <DataList.Value>
                        {fromUnixTime(
                          subscriptions.subscription.start_date,
                        ).toLocaleDateString("en-GB")}
                      </DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label minWidth="88px">Expires</DataList.Label>
                      <DataList.Value>
                        {fromUnixTime(
                          subscriptions.subscription.cancel_at,
                        ).toLocaleDateString("en-GB")}
                      </DataList.Value>
                    </DataList.Item>
                  </>
                )}
              </DataList.Root>
            </CardContent>

            <CardFooter className="border-t px-6 py-4 flex justify-end">
              {!subscriptions.subscription.cancel_at && (
                <Dialog open={open} onOpenChange={(val) => handleDialog(val)}>
                  <DialogTrigger asChild>
                    <Button variant="destructive" type="button" className="">
                      Cancel Subscription
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <form>
                      <DialogHeader>
                        <DialogTitle className="mb-4">
                          Are you sure?
                        </DialogTitle>
                      </DialogHeader>
                      {/* {checkError(profileState) && (
                  <Alert variant="destructive" className="mb-4 mt-4">
                    <AlertTriangle className="h-6 w-6" />
                    <AlertTitle className="!pl-8">Error</AlertTitle>
                    <AlertDescription className="!pl-8">
                      {profileState?.error}
                    </AlertDescription>
                  </Alert>
                )} */}
                      <p>
                        Are you sure you want to cancel your subscription? If
                        you do, you{`'`}ll lose access on{" "}
                        {fromUnixTime(
                          subscriptions.subscription.current_period_end,
                        ).toLocaleDateString("en-GB")}
                        .
                      </p>
                      <Flex direction="column" className="mt-2"></Flex>
                      <DialogFooter className="mt-4">
                        <Button
                          variant="destructive"
                          type="button"
                          onClick={() => cancelSubscription()}
                        >
                          Cancel Subscription
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
              {subscriptions.subscription.cancel_at && (
                <Button
                  variant="default"
                  type="button"
                  className=""
                  onClick={() => resumeSubscription()}
                >
                  Resume
                </Button>
              )}
            </CardFooter>
          </>
        )}
      </ShineBorder>
    </>
  );
}
